<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useCommercialDashboardStore } from '@/stores/useCommercialDashboardStore'
import { formatAmount, formatDate } from './dashboardFormat'

const store = useCommercialDashboardStore()
const { scopedRenewals, scopedStats, renewalsOverdue, renewalsSoon, renewalsUpcoming } = storeToRefs(store)

function daysUntil(iso: string): number {
  return Math.floor((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
}
</script>

<template>
  <div v-if="scopedRenewals.length > 0" class="card bg-base-100 shadow">
    <div class="card-body p-5">
      <div class="flex items-center justify-between mb-4">
        <h2 class="card-title text-base">Renouvellements à venir</h2>
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold text-primary">{{ formatAmount(scopedStats.renewalValue) }} à renouveler</span>
          <span class="badge badge-neutral">{{ scopedRenewals.length }} contrats</span>
        </div>
      </div>

      <!-- Sans engagement -->
      <div v-if="renewalsOverdue.length > 0" class="mb-4">
        <div class="flex items-center gap-2 mb-2">
          <span class="badge bg-red-100 text-red-700 border-0 font-semibold">● Sans engagement</span>
          <span class="text-sm opacity-50">{{ renewalsOverdue.length }} clients</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="renewal in renewalsOverdue"
            :key="renewal.quoteUuid"
            class="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-3 py-2"
          >
            <div>
              <p class="text-sm font-semibold">{{ renewal.companyName }}</p>
              <p class="text-xs opacity-50">
                Échu depuis {{ Math.abs(daysUntil(renewal.contractEndDate)) }} jours ·
                Contrat {{ renewal.subscriptionDurationCode ?? '?' }} mois
                <span v-if="renewal.outstandingAmount > 0" class="text-error font-medium">
                  · {{ formatAmount(renewal.outstandingAmount) }} impayés
                </span>
              </p>
            </div>
            <div class="text-right shrink-0">
              <p class="text-sm font-bold">{{ formatAmount(renewal.totalCa) }}/an</p>
              <p class="text-xs text-error font-semibold">Rappel urgent</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Dans 30 jours -->
      <div v-if="renewalsSoon.length > 0" class="mb-4">
        <div class="flex items-center gap-2 mb-2">
          <span class="badge bg-orange-100 text-orange-700 border-0 font-semibold">● Dans 30 jours</span>
          <span class="text-sm opacity-50">{{ renewalsSoon.length }} clients</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="renewal in renewalsSoon"
            :key="renewal.quoteUuid"
            class="flex items-center justify-between rounded-xl border border-orange-200 bg-orange-50 px-3 py-2"
          >
            <div>
              <p class="text-sm font-semibold">{{ renewal.companyName }}</p>
              <p class="text-xs opacity-50">
                Échéance le {{ formatDate(renewal.contractEndDate) }} ·
                Contrat {{ renewal.subscriptionDurationCode ?? '?' }} mois
                <span v-if="renewal.outstandingAmount > 0" class="text-error font-medium">
                  · {{ formatAmount(renewal.outstandingAmount) }} impayés
                </span>
              </p>
            </div>
            <div class="text-right shrink-0">
              <p class="text-sm font-bold">{{ formatAmount(renewal.totalCa) }}/an</p>
              <p class="text-xs text-orange-600 font-semibold">À traiter</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Dans 90 jours -->
      <div v-if="renewalsUpcoming.length > 0">
        <div class="flex items-center gap-2 mb-2">
          <span class="badge bg-yellow-100 text-yellow-700 border-0 font-semibold">● Dans 90 jours</span>
          <span class="text-sm opacity-50">{{ renewalsUpcoming.length }} clients</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="renewal in renewalsUpcoming"
            :key="renewal.quoteUuid"
            class="flex items-center justify-between rounded-xl border border-base-300 bg-base-100 px-3 py-2"
          >
            <div>
              <p class="text-sm font-semibold">{{ renewal.companyName }}</p>
              <p class="text-xs opacity-50">
                Échéance le {{ formatDate(renewal.contractEndDate) }} ·
                Contrat {{ renewal.subscriptionDurationCode ?? '?' }} mois
              </p>
            </div>
            <div class="text-right shrink-0">
              <p class="text-sm font-bold">{{ formatAmount(renewal.totalCa) }}/an</p>
              <p class="text-xs opacity-40">À anticiper</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
